/**
 * CODELTY Product Card
 */

class CodeltyProductCards {
  static initialized = false;
  static reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  static mobileQuery = window.matchMedia('(max-width: 749px)');
  static viewportObserver = null;
  static controllers = new WeakMap();

  static init() {
    if (CodeltyProductCards.initialized) return;
    CodeltyProductCards.initialized = true;

    document.addEventListener('pointerenter', CodeltyProductCards.handlePointerEnter, true);
    document.addEventListener('pointerleave', CodeltyProductCards.handlePointerLeave, true);
    document.addEventListener('click', CodeltyProductCards.handleClick, true);

    CodeltyProductCards.setupViewportObserver();
    CodeltyProductCards.mobileQuery.addEventListener('change', CodeltyProductCards.handleMobileChange);
    CodeltyProductCards.scan(document);
  }

  static getController(card) {
    let controller = CodeltyProductCards.controllers.get(card);
    if (!controller) {
      controller = new CodeltyMediaController(card);
      CodeltyProductCards.controllers.set(card, controller);
    }
    return controller;
  }

  static handleMobileChange() {
    CodeltyProductCards.setupViewportObserver();
    document.querySelectorAll('[data-codelty-product-card][data-codelty-ready]').forEach((card) => {
      const controller = CodeltyProductCards.getController(card);
      controller.refresh();
      if (CodeltyProductCards.reducedMotion) {
        controller.setState('idle');
      } else if (CodeltyProductCards.mobileQuery.matches) {
        controller.setState('idle');
      } else {
        controller.setState('normal');
      }
    });
  }

  static setupViewportObserver() {
    if (CodeltyProductCards.viewportObserver) {
      CodeltyProductCards.viewportObserver.disconnect();
      CodeltyProductCards.viewportObserver = null;
    }

    if (!CodeltyProductCards.mobileQuery.matches || CodeltyProductCards.reducedMotion) return;

    CodeltyProductCards.viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const controller = CodeltyProductCards.getController(entry.target);
          controller.refresh();
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            controller.setState('viewport');
          } else {
            controller.setState('idle');
          }
        });
      },
      { threshold: [0, 0.55, 0.85] }
    );

    document.querySelectorAll('[data-codelty-product-card][data-codelty-ready]').forEach((card) => {
      CodeltyProductCards.viewportObserver.observe(card);
    });
  }

  static scan(root) {
    root.querySelectorAll('[data-codelty-product-card]:not([data-codelty-ready])').forEach((card) => {
      card.setAttribute('data-codelty-ready', '');
      CodeltyProductCards.bindMediaLoad(card);

      const controller = CodeltyProductCards.getController(card);
      controller.refresh();

      if (CodeltyProductCards.reducedMotion) {
        controller.setState('idle');
      } else if (CodeltyProductCards.mobileQuery.matches) {
        controller.setState('idle');
      } else {
        controller.setState('normal');
      }

      if (CodeltyProductCards.viewportObserver) {
        CodeltyProductCards.viewportObserver.observe(card);
      }
    });
  }

  static bindMediaLoad(card) {
    const media = card.querySelector('.codelty-product-card__media');
    if (!media) return;

    const images = media.querySelectorAll('.codelty-product-card__image--primary');
    const markLoaded = () => {
      media.classList.remove('codelty-product-card__media--loading');
      media.classList.add('is-loaded');
    };

    if (images.length === 0) {
      markLoaded();
      return;
    }

    let pending = images.length;
    const onDone = () => {
      pending -= 1;
      if (pending === 0) markLoaded();
    };

    images.forEach((img) => {
      if (img.complete) {
        onDone();
        return;
      }
      img.addEventListener('load', onDone, { once: true });
      img.addEventListener('error', onDone, { once: true });
    });
  }

  static getCard(target) {
    return target.closest('[data-codelty-product-card]');
  }

  static handlePointerEnter(event) {
    if (CodeltyProductCards.reducedMotion || CodeltyProductCards.mobileQuery.matches) return;
    const card = CodeltyProductCards.getCard(event.target);
    if (!card) return;

    const controller = CodeltyProductCards.getController(card);
    controller.refresh();

    if (controller.hasSecondary) {
      controller.setState('hover');
    } else {
      controller.setState('normal');
    }
  }

  static handlePointerLeave(event) {
    if (CodeltyProductCards.reducedMotion || CodeltyProductCards.mobileQuery.matches) return;
    const card = CodeltyProductCards.getCard(event.target);
    if (!card) return;
    if (card.contains(event.relatedTarget)) return;

    CodeltyProductCards.getController(card).setState('normal');
  }

  static ensureQuickAddModal(card) {
    const template = card.querySelector('template[data-codelty-quick-add-modal]');
    const opener = card.querySelector('modal-opener[data-modal^="#QuickAdd-"]');
    if (!template || !opener) return null;

    const modalSelector = opener.getAttribute('data-modal');
    let modal = document.querySelector(modalSelector);
    if (modal) return modal;

    modal = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(modal);
    return modal;
  }

  static ensureQuickViewModal(card) {
    const template = card.querySelector('template[data-codelty-quick-view-modal]');
    const opener = card.querySelector('modal-opener[data-modal^="#QuickView-"]');
    if (!template || !opener) return null;

    const modalSelector = opener.getAttribute('data-modal');
    let modal = document.querySelector(modalSelector);
    if (modal) return modal;

    modal = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(modal);
    return modal;
  }

  static previewSwatch(card, link) {
    const imageUrl = link.dataset.variantImage;
    if (!imageUrl) return;

    const primaryImage = card.querySelector('.codelty-product-card__image--primary');
    if (!primaryImage) return;

    if (!primaryImage.dataset.codeltyDefaultSrc) {
      primaryImage.dataset.codeltyDefaultSrc = primaryImage.currentSrc || primaryImage.src;
    }

    primaryImage.src = imageUrl;
    primaryImage.srcset = '';
  }

  static resetSwatchPreview(card) {
    const primaryImage = card.querySelector('.codelty-product-card__image--primary');
    if (!primaryImage || !primaryImage.dataset.codeltyDefaultSrc) return;
    primaryImage.src = primaryImage.dataset.codeltyDefaultSrc;
  }

  static handleClick(event) {
    const swatchLink = event.target.closest('.codelty-product-card__swatch-link');
    if (swatchLink) return;

    const viewButton = event.target.closest('.codelty-product-card__action-btn--view');
    if (viewButton) {
      const card = CodeltyProductCards.getCard(viewButton);
      if (card) CodeltyProductCards.ensureQuickViewModal(card);
      return;
    }

    const quickAddButton = event.target.closest('.codelty-product-card__quick-add-btn');
    if (!quickAddButton) return;

    const card = CodeltyProductCards.getCard(quickAddButton);
    if (!card) return;

    const opener = quickAddButton.closest('modal-opener');
    if (opener) {
      CodeltyProductCards.ensureQuickAddModal(card);
    }

    const form = quickAddButton.closest('product-form form');
    if (form && quickAddButton.type === 'submit') {
      quickAddButton.classList.add('loading');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => CodeltyProductCards.init());

document.addEventListener('shopify:section:load', (event) => {
  CodeltyProductCards.scan(event.target);
});

document.addEventListener(
  'pointerenter',
  (event) => {
    const link = event.target.closest('.codelty-product-card__swatch-link');
    if (!link) return;
    const card = CodeltyProductCards.getCard(link);
    if (card) CodeltyProductCards.previewSwatch(card, link);
  },
  true
);

document.addEventListener(
  'pointerleave',
  (event) => {
    const swatches = event.target.closest('[data-codelty-swatches]');
    if (!swatches) return;
    const card = CodeltyProductCards.getCard(swatches);
    if (card) CodeltyProductCards.resetSwatchPreview(card);
  },
  true
);
